import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Elements, OnLoadParams, Node} from 'react-flow-renderer';
import {
  Employee,
  useContainerContext,
  RoomType,
  useService,
  useSubscription,
  Workspace,
  WorkspaceStatuses,
  AttendanceStatistics,
  WorkspacesApiService,
  RoomsApiService,
  DoorsApiService,
  WorkspaceResponse,
} from '@innowise-group/core';
import {getApiErrorCode, isWorkspaceRoomBoundsErrorCode} from '@shared/core/utilities/api-error.utility';
import {useTranslation} from 'react-i18next';
import {combineLatest, finalize, map, Observable, of} from 'rxjs';
import {concatMap, switchMap, tap} from 'rxjs/operators';
import {SearchFloorValues} from '../../components/search-floor-form';
import {BookingWorkspaceModeFormValues} from '../../components/booking-workspace-mode-form';
import {FloorsDataManagerService} from '../../services/floors-data-manager';
import {
  ErrorDataType,
  FloorOutboundEvent,
  FloorsEventEmitterService,
  UpdatedMeetingRoomDataType,
  WorkspacesChangedDataType,
  WorkspaceChangedDataType,
  RoomChangedDataType,
  RoomDeletedDataType,
  DoorDeletedDataType,
} from '../../services/floors-event-emitter';
import {FloorsModalsFacadeService} from '../../services/floors-modals-facade';
import {AddWorkspaceValues} from '../../components/add-workspace-form';
import {
  editWorkspaceUtility,
  updateWorkspaceData,
  UpdatedWorkspaceDataResult,
  createDraftWorkspaceNode,
  requiresWholeFloorRefresh,
  buildAbortWorkspaceUpdates,
  toAbortUpdatePayload,
} from '../../utilities';
import {toWorkspaceCreatePayload, WorkspaceMovePayload} from '../../utilities/update-workspace-move-data.utility';
import {WorkspaceElementData} from '../../components/workspace-view';
import {PlanElementData} from '../../components/plan-element-view';
import {getProcessedBookedRange, getRelevantBookedRange} from '../../utilities/get-relevant-booked-range.utility';

import {useWorkspaceBooking} from './use-workspace-booking.hook';
import {useKeyPress} from './use-key-press.hook';
import {useFloorReport} from './use-floor-report.hook';
import {FloorData, usePlanDetails} from './use-plan-details.hook';
import {useFloorEvents} from './use-floor-events.hook';
import {useFlowElementsDrag, PatchWorkspaceOptions} from './use-flow-elements-drag.hook';
import {useFlowElements} from './use-flow-elements.hook';
import {determineRoomMeetingId} from '@shared/utilities';

interface UseFloorDetailsResult {
  flowElements: Elements;
  workspaceElements: Elements<WorkspaceElementData>;
  roomElements: Elements<PlanElementData>;
  floor: FloorData;
  handleAddNewWorkspace: (isVirtual: boolean, rotateDegree?: number) => void;
  isPlanExists: boolean;
  isPlanLoading: boolean;
  isAttendanceLoading: boolean;
  reactFlowWrapper: React.MutableRefObject<HTMLDivElement>;
  onLoad: (_reactFlowInstance: OnLoadParams) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handleUploadPlan: () => void;
  onNodeDragStart: (event: React.MouseEvent<Element, MouseEvent>, node: Node) => void;
  onNodeDragStop: (event: React.MouseEvent<Element, MouseEvent>, node: Node) => void;
  handleSearchFloorSubmit: (data: SearchFloorValues) => void;
  handleBookingWorkspaceMode: (data: BookingWorkspaceModeFormValues) => void;
  handleBookWorkspace: () => void;
  selectable: boolean;
  abortChanges: () => Observable<void>;
  saveChanges: () => boolean;
  floorsEventEmitter: FloorsEventEmitterService;
  isClientReadyState: boolean;
  handleSecondPlanFloorChange: () => void;
  isWorkspaceMutationPending: boolean;
  beginWorkspaceMutation: () => void;
  endWorkspaceMutation: () => void;
  hasDraftWorkspace: boolean;
}

export const useFloorDetails = (
  floorId: string,
  isViewMode: boolean,
  setEmployees: React.Dispatch<React.SetStateAction<Employee[]>>,
  showNotifications: boolean,
  setAttendanceStatistics?: React.Dispatch<React.SetStateAction<AttendanceStatistics>>,
): UseFloorDetailsResult => {
  const [selectable, setSelectable] = useState(true);
  const [pendingWorkspaceMutations, setPendingWorkspaceMutations] = useState(0);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const pendingWorkspaceIdsRef = useRef<Set<number>>(new Set());
  const skipWorkspacesChangedRef = useRef(0);
  const floorRefreshInFlightRef = useRef(false);

  const beginWorkspaceMutation = useCallback(() => {
    setPendingWorkspaceMutations((count) => count + 1);
  }, []);

  const endWorkspaceMutation = useCallback(() => {
    setPendingWorkspaceMutations((count) => Math.max(0, count - 1));
  }, []);

  const isWorkspaceMutationPending = pendingWorkspaceMutations > 0;

  const container = useContainerContext();
  const {
    planElements$,
    workspaces$,
    rooms$,
    initialWorkspaces$,
    createdWorkspaces$,
    deletedWorkspaces$,
    setWorkspacesSubscription,
    deleteWorkspaceSubscription,
    createWorkspaceSubscription,
    editWorkspaceSubscription,
    errorSubscription,
    editRoomSubscription,
    deleteRoomSubscription,
    deleteDoorSubscription,
    initialize,
    applyViewMode,
    updateMeetingRoomInRooms,
  } = useMemo<FloorsDataManagerService>(() => container.get(FloorsDataManagerService.type), [container]);
  const workspaces = useSubscription(workspaces$);
  const planElements = useSubscription(planElements$);
  const rooms = useSubscription(rooms$);
  const initialWorkspaces = useSubscription(initialWorkspaces$);
  const createdWorkspaces = useSubscription(createdWorkspaces$);
  const deletedWorkspaces = useSubscription(deletedWorkspaces$);
  const floorsModalsFacade = useService(FloorsModalsFacadeService);
  const workspacesApi = useService(WorkspacesApiService);
  const roomsApi = useService(RoomsApiService);
  const doorsApi = useService(DoorsApiService);
  const floorsEventEmitter = useMemo<FloorsEventEmitterService>(
    () => container.get(FloorsEventEmitterService.type),
    [container],
  );

  // Process Space key press
  useKeyPress('Space', setSelectable);

  // Get plan details
  const {
    floor,
    isPlanLoading,
    isPlanExists,
    setIsPlanLoading,
    uploadPlanHandler,
    refreshAttendanceStatistics,
    isAttendanceLoading,
  } = usePlanDetails(floorId, initialize, isViewMode, setAttendanceStatistics);

  const bumpTotalWorkspaces = useCallback(
    (delta: number) => {
      if (!setAttendanceStatistics) {
        return;
      }
      setAttendanceStatistics((prev) => ({
        ...prev,
        total_workspaces: Math.max(0, (prev?.total_workspaces ?? 0) + delta),
        // New desks are created as Free; on delete we wait for attendance refresh.
        ...(delta > 0 ? {free_workspaces: Math.max(0, (prev?.free_workspaces ?? 0) + delta)} : null),
      }));
    },
    [setAttendanceStatistics],
  );

  const syncLiveWorkspaces = useCallback(() => {
    workspacesApi.getWorkspacesByFloor(floorId).subscribe({
      next: (workspaces) => {
        // Drafts are preserved inside setWorkspacesSubscription.
        setWorkspacesSubscription(workspaces, isViewMode);
      },
      error: (error) => errorSubscription(500, String(error), showNotifications),
    });
  }, [floorId, workspacesApi, setWorkspacesSubscription, isViewMode, errorSubscription, showNotifications]);

  // Handle booking statuses (syncWorkspaces is mode-aware: live list vs booking-mode projection).
  const {
    bookingWorkspaceMode,
    bookWorkspace,
    bookChosenWorkspace,
    bookWorkspaceDateRange,
    syncWorkspaces,
    isBookingModeActive,
  } = useWorkspaceBooking(floor, setIsPlanLoading, floorId, workspaces$, syncLiveWorkspaces, (error) =>
    errorSubscription(500, String(error), showNotifications),
  );

  const refreshWorkspaces = syncWorkspaces;

  const reconcileAfterRoomDelete = useCallback(
    (roomId: string, doorIds: Array<string | number> = []) => {
      const roomIdStr = String(roomId);
      const roomStillPresent = rooms$
        .getValue()
        .some((element) => element.id === `rooms-${roomIdStr}` || String(element.data?.id) === roomIdStr);

      // Local handler already pruned this room; socket echo only re-syncs with server.
      if (!roomStillPresent) {
        refreshWorkspaces();
        refreshAttendanceStatistics();
        return;
      }

      const beforeCount = workspaces$.getValue().filter((element) => !element.data?.isDraft).length;
      deleteRoomSubscription(roomId, doorIds);
      const afterCount = workspaces$.getValue().filter((element) => !element.data?.isDraft).length;
      const desksRemoved = Math.max(0, beforeCount - afterCount);

      setAttendanceStatistics?.((prev) => ({
        ...prev,
        rooms_count: Math.max(0, (prev?.rooms_count ?? 0) - 1),
        total_workspaces: Math.max(0, (prev?.total_workspaces ?? 0) - desksRemoved),
      }));

      refreshWorkspaces();
      refreshAttendanceStatistics();
    },
    [
      deleteRoomSubscription,
      refreshAttendanceStatistics,
      refreshWorkspaces,
      rooms$,
      setAttendanceStatistics,
      workspaces$,
    ],
  );

  const markPendingWorkspace = useCallback((workspaceId: number) => {
    pendingWorkspaceIdsRef.current.add(workspaceId);
  }, []);

  const consumePendingWorkspace = useCallback((workspaceId: number) => {
    if (!pendingWorkspaceIdsRef.current.has(workspaceId)) {
      return false;
    }
    pendingWorkspaceIdsRef.current.delete(workspaceId);
    return true;
  }, []);

  const setWorkspaceSaving = useCallback(
    (workspaceId: string | number, isSaving: boolean) => {
      const workspaceIdStr = String(workspaceId);
      const current = workspaces$.getValue();
      workspaces$.next(
        current.map((element) =>
          String(element.data.id) === workspaceIdStr || element.id === workspaceIdStr
            ? {
                ...element,
                data: {...element.data, isSaving},
                draggable: isSaving ? false : !isViewMode,
              }
            : element,
        ),
      );
    },
    [isViewMode, workspaces$],
  );

  const removeDraftWorkspace = useCallback(
    (draftId: string) => {
      const current = workspaces$.getValue();
      workspaces$.next(current.filter((element) => element.id !== draftId && element.data.id !== draftId));
    },
    [workspaces$],
  );

  const applyWorkspaceCreate = useCallback(
    (workspace: WorkspaceResponse, draftIdToReplace?: string) => {
      if (draftIdToReplace) {
        removeDraftWorkspace(draftIdToReplace);
      }

      const roomId = workspace.room != null ? String(workspace.room) : null;
      const roomExists = Boolean(roomId) && rooms$.getValue().some((element) => String(element.data?.id) === roomId);

      // Race: room deleted while create was in-flight — drop ghost and re-sync with server.
      if (!roomExists && !floor?.isVirtual) {
        refreshWorkspaces();
        refreshAttendanceStatistics();
        return;
      }

      markPendingWorkspace(workspace.id);
      createWorkspaceSubscription(workspace, isViewMode, showNotifications);
      bumpTotalWorkspaces(1);
      refreshAttendanceStatistics();
    },
    [
      bumpTotalWorkspaces,
      createWorkspaceSubscription,
      floor?.isVirtual,
      isViewMode,
      markPendingWorkspace,
      refreshAttendanceStatistics,
      refreshWorkspaces,
      removeDraftWorkspace,
      rooms$,
      showNotifications,
    ],
  );

  const commitDraftWorkspaceRef = useRef<(node: Node<WorkspaceElementData>) => void>(() => undefined);

  const applyWorkspaceUpdate = useCallback(
    (workspace: WorkspaceResponse, withNotification = false) => {
      markPendingWorkspace(workspace.id);
      editWorkspaceSubscription(workspace, isViewMode, withNotification, showNotifications);
    },
    [editWorkspaceSubscription, isViewMode, markPendingWorkspace, showNotifications],
  );

  const revertWorkspacePosition = useCallback(
    (workspaceId: string | number, coordinates: Array<number[]>, position?: {x: number; y: number}) => {
      const workspaceIdStr = String(workspaceId);
      const [centerX, centerY] = coordinates[0];
      const revertedPosition = position ?? {
        x: centerX - 50,
        y: centerY - 25,
      };
      const current = workspaces$.getValue();
      const revertedElement = current.find((element) => String(element.data.id) === workspaceIdStr);

      if (!revertedElement) {
        return;
      }

      const restoredElement = {
        ...revertedElement,
        position: revertedPosition,
        data: {
          ...revertedElement.data,
          coordinates,
          isSaving: false,
        },
        draggable: !isViewMode,
      };
      const withoutMovedWorkspace = current.filter((element) => String(element.data.id) !== workspaceIdStr);

      workspaces$.next(withoutMovedWorkspace);
      requestAnimationFrame(() => {
        workspaces$.next([...withoutMovedWorkspace, restoredElement]);
      });
    },
    [isViewMode, workspaces$],
  );

  const patchWorkspace = useCallback(
    (workspaceData: UpdatedWorkspaceDataResult | WorkspaceMovePayload, options?: PatchWorkspaceOptions) => {
      const workspaceId = workspaceData.id;
      const status = 'status' in workspaceData ? workspaceData.status : undefined;
      const employeeId = 'employee' in workspaceData ? workspaceData.employee : undefined;
      const shouldRefreshFloor = options?.refreshFloor ?? (requiresWholeFloorRefresh(status) && employeeId != null);

      setWorkspaceSaving(workspaceId, true);

      if (shouldRefreshFloor) {
        floorRefreshInFlightRef.current = true;
        skipWorkspacesChangedRef.current += 1;
        markPendingWorkspace(Number(workspaceId));
      }

      workspacesApi
        .patchWorkspace(String(workspaceId), workspaceData)
        .pipe(
          concatMap(() =>
            shouldRefreshFloor
              ? workspacesApi.getWorkspacesByFloor(floorId)
              : workspacesApi.getWorkspaceResponseById(String(workspaceId)),
          ),
        )
        .subscribe({
          next: (response) => {
            // Occupancy changes move the floor "Free desks" / attendance counters (HTLGT-2121/2123).
            if (requiresWholeFloorRefresh(status)) {
              refreshAttendanceStatistics();
            }

            if (shouldRefreshFloor) {
              floorRefreshInFlightRef.current = false;
              setWorkspacesSubscription(response as WorkspaceResponse[], isViewMode);
              return;
            }

            applyWorkspaceUpdate(response as WorkspaceResponse);
          },
          error: (error) => {
            floorRefreshInFlightRef.current = false;
            consumePendingWorkspace(Number(workspaceId));

            if (options?.revertOnError && options.previousCoordinates) {
              revertWorkspacePosition(workspaceId, options.previousCoordinates, options.previousPosition);
              workspacesApi.getWorkspaceResponseById(String(workspaceId)).subscribe({
                next: (workspace) => editWorkspaceSubscription(workspace, isViewMode, false, showNotifications),
                error: () => setWorkspaceSaving(workspaceId, false),
              });
            } else {
              setWorkspaceSaving(workspaceId, false);
            }

            // Friendly toast for this case is already shown by the HTTP interceptor.
            if (isWorkspaceRoomBoundsErrorCode(getApiErrorCode(error))) {
              return;
            }

            errorSubscription(500, String(error), showNotifications);
          },
        });
    },
    [
      workspacesApi,
      applyWorkspaceUpdate,
      editWorkspaceSubscription,
      errorSubscription,
      setWorkspaceSaving,
      showNotifications,
      revertWorkspacePosition,
      isViewMode,
      floorId,
      setWorkspacesSubscription,
      markPendingWorkspace,
      consumePendingWorkspace,
      refreshAttendanceStatistics,
    ],
  );

  // Handle Drag events
  const {
    nodeDragStartHandler,
    nodeDragStopHandler,
    dragHandler,
    dragOverHandler,
    reactFlowWrapper,
    loadHandler,
    hasInvalidWorkspaces,
    setValidation,
    resetValidation,
    getViewportCenter,
  } = useFlowElementsDrag(
    workspaces,
    rooms,
    floor?.isVirtual,
    bookWorkspaceDateRange,
    patchWorkspace,
    (node) => commitDraftWorkspaceRef.current(node),
    workspacesApi,
    floorsEventEmitter,
    (error) => errorSubscription(500, String(error), showNotifications),
    revertWorkspacePosition,
  );

  const addDraftWorkspace = useCallback(
    (node: Node<WorkspaceElementData>) => {
      const current = workspaces$.getValue();
      if (current.some((element) => element.data?.isDraft)) {
        return;
      }
      workspaces$.next([...current, node]);
      setValidation();
    },
    [workspaces$, setValidation],
  );

  const commitDraftWorkspace = useCallback(
    (node: Node<WorkspaceElementData>) => {
      const draftId = node.id;
      const payload = toWorkspaceCreatePayload(node.data, floorId);
      const current = workspaces$.getValue();
      workspaces$.next(
        current.map((element) =>
          element.id === draftId
            ? {
                ...node,
                data: {...node.data, isSaving: true},
                draggable: false,
              }
            : element,
        ),
      );
      beginWorkspaceMutation();
      workspacesApi
        .createWorkspace(payload)
        .pipe(finalize(() => endWorkspaceMutation()))
        .subscribe({
          next: (created) => {
            applyWorkspaceCreate(created, draftId);
            resetValidation();
          },
          error: (error) => {
            setWorkspaceSaving(draftId, false);
            setValidation();
            errorSubscription(500, String(error), showNotifications);
          },
        });
    },
    [
      applyWorkspaceCreate,
      beginWorkspaceMutation,
      endWorkspaceMutation,
      errorSubscription,
      floorId,
      resetValidation,
      setValidation,
      setWorkspaceSaving,
      showNotifications,
      workspaces$,
      workspacesApi,
    ],
  );
  commitDraftWorkspaceRef.current = commitDraftWorkspace;

  // Get reports methods
  const {getCSVReport, getPDFReport, getXLSXReport} = useFloorReport(floor, reactFlowWrapper);

  const [workspacesToRevert, setWorkspacesToRevert] = useState<Workspace[]>([]);

  const [isClientReadyState, setIsClientReadyState] = useState(false);

  const handleClientState = () => {
    setIsClientReadyState(true);
  };

  useEffect(() => {
    floorsEventEmitter.addListener('clientReady', handleClientState);

    return () => {
      floorsEventEmitter.removeListener('clientReady', handleClientState);
    };
  }, [floorId, floorsEventEmitter]);

  const handleEditWorkspace = useCallback(
    (data: Workspace) => {
      const {number, range, employee, status, bookedStatus, id, ...rest} = data;

      const processedBookedRange = getProcessedBookedRange(bookedStatus);

      const relevantBooking = getRelevantBookedRange(processedBookedRange);

      const startDay = relevantBooking ? relevantBooking?.range.start : range?.startDay;
      const endDay = relevantBooking ? relevantBooking?.range.end : range?.endDay;

      floorsModalsFacade.openWorkspaceActionsModal(
        (values: AddWorkspaceValues & {selectedData: string}) => {
          const editedWorkspace = editWorkspaceUtility(data, values);
          const updatedWorkspace = updateWorkspaceData(editedWorkspace);
          const assignedEmployeeId = values?.employee ?? employee?.id;

          patchWorkspace(updatedWorkspace, {refreshFloor: Boolean(assignedEmployeeId)});

          if (assignedEmployeeId) {
            setEmployees((employees) => employees.filter(({id}) => id !== assignedEmployeeId));
          }
        },
        floor?.address,
        {
          id: id,
          number,
          dateRange: [startDay ? new Date(startDay) : null, endDay ? new Date(endDay) : null],
          employee: relevantBooking ? relevantBooking?.employeeId : employee?.id,
          status: status,
          ...rest,
        },
        `${t('modals.workspace')} ${number ? `№${number}` : ''}`,
        floor?.isVirtual,
        processedBookedRange,
        setWorkspacesToRevert,
      );
    },
    [floorsModalsFacade, t, floor.address, floor.isVirtual, setEmployees, patchWorkspace],
  );

  const handleEditRoom = useCallback(
    (roomId: string, type: RoomType, number: string, isOur: boolean, meetingRoomId?: string) => {
      floorsModalsFacade.openEditRoomFormModal(
        (values) => {
          const meetingRoomData = determineRoomMeetingId(values);
          return roomsApi.changeRoom(roomId, {
            number: values.number,
            room_type: values.type,
            our: values.isOur,
            ...meetingRoomData,
          });
        },
        {isOur, number, type, meetingRoomId},
        t('modals.editRoom'),
        () =>
          floorsModalsFacade
            .openDeleteRoomConfirmationModal(
              t('modals.deleteRoomTitle', {number}),
              t('modals.deleteRoomText', {number}),
              t('modals.deleteRoomConfirmationText'),
              t('buttons.cancel'),
            )
            .pipe(
              switchMap((deleteDoors) => roomsApi.deleteRoom(roomId, {deleteDoors})),
              tap((result) => {
                // Local prune first, then always reconcile — do not skip workspaces_changed self-heal.
                reconcileAfterRoomDelete(roomId, result?.door_ids || []);
              }),
              map(() => undefined),
            ),
      );
    },
    [floorsModalsFacade, t, roomsApi, reconcileAfterRoomDelete],
  );

  const handleDeleteWorkspace = useCallback(
    (workspace: Workspace & {isDraft?: boolean}) => {
      const {id, number, status, room, activeRequests, isDraft} = workspace;

      if (isDraft || String(id).startsWith('draft-')) {
        removeDraftWorkspace(String(id));
        resetValidation();
        return;
      }

      const baseText = t('modals.deleteWorkspaceText', {number, room: room.number});

      let extraText = '';

      if (status === WorkspaceStatuses.Occupied || status === WorkspaceStatuses.Remote) {
        extraText = t('modals.deleteWorkspaceWarning');
      } else if (status === WorkspaceStatuses.Booked || (activeRequests && activeRequests.length > 0)) {
        extraText = t('modals.deleteBookedWorkspaceText');
      }

      floorsModalsFacade
        .openConfirmationModal(
          t('modals.deleteWorkspaceTitle', {number}),
          `${baseText}\n${extraText}`,
          t('modals.deleteWorkspaceConfirmationText'),
          t('buttons.cancel'),
        )
        .subscribe(() => {
          beginWorkspaceMutation();
          workspacesApi
            .deleteWorkspace(String(id))
            .pipe(finalize(() => endWorkspaceMutation()))
            .subscribe({
              next: () => {
                skipWorkspacesChangedRef.current += 1;
                deleteWorkspaceSubscription(
                  {
                    id: Number(id),
                    number: Number(number),
                    status,
                    room: room?.id ? Number(room.id) : null,
                    room_number: room?.number,
                    room_type: room?.room_type,
                    employee: null,
                    occupation_end_date: null,
                    occupation_start_date: null,
                    occupation_type: null,
                    comment: null,
                    coordinates: workspace.coordinates,
                    booked_status: [],
                    active_requests: [],
                    rotate_degree: workspace.rotateDegree,
                  } as WorkspaceResponse,
                  isViewMode,
                  String(id),
                  showNotifications,
                );
                bumpTotalWorkspaces(-1);
                refreshAttendanceStatistics();
              },
              error: (error) => errorSubscription(500, String(error), showNotifications),
            });
          resetValidation();
        });
    },
    [
      resetValidation,
      floorsModalsFacade,
      t,
      workspacesApi,
      deleteWorkspaceSubscription,
      beginWorkspaceMutation,
      endWorkspaceMutation,
      isViewMode,
      errorSubscription,
      showNotifications,
      removeDraftWorkspace,
      bumpTotalWorkspaces,
      refreshAttendanceStatistics,
    ],
  );

  const handleDeleteDoor = useCallback(
    (doorId: string) => {
      floorsModalsFacade
        .openConfirmationModal(
          t('modals.deleteDoorTitle'),
          t('modals.deleteDoorText'),
          t('modals.deleteDoorConfirmationText'),
          t('buttons.cancel'),
        )
        .subscribe(() => {
          doorsApi.deleteDoor(doorId).subscribe({
            next: () => deleteDoorSubscription(doorId),
            error: (error) => errorSubscription(500, String(error), showNotifications),
          });
        });
    },
    [floorsModalsFacade, t, doorsApi, deleteDoorSubscription, errorSubscription, showNotifications],
  );

  const eventsMap = useMemo(() => {
    return {
      [FloorOutboundEvent.WORKSPACES_CHANGED]: (_data: WorkspacesChangedDataType) => {
        if (floorRefreshInFlightRef.current) {
          return;
        }
        if (skipWorkspacesChangedRef.current > 0) {
          skipWorkspacesChangedRef.current -= 1;
          return;
        }
        // Mode-aware: booking-mode → booking-mode API; otherwise live list.
        refreshWorkspaces();
      },
      [FloorOutboundEvent.WORKSPACE_CHANGED]: ({workspace_id}: WorkspaceChangedDataType) => {
        if (floorRefreshInFlightRef.current) {
          consumePendingWorkspace(workspace_id);
          return;
        }
        if (consumePendingWorkspace(workspace_id)) {
          return;
        }
        // In booking-mode never apply live single-desk patch — full booking projection re-fetch.
        if (isBookingModeActive()) {
          refreshWorkspaces();
          return;
        }
        workspacesApi.getWorkspaceResponseById(String(workspace_id)).subscribe({
          next: (workspace) => {
            const exists = workspaces$.getValue().some((item) => item.data.id === String(workspace.id));
            if (exists) {
              editWorkspaceSubscription(workspace, isViewMode, false, showNotifications);
            } else {
              createWorkspaceSubscription(workspace, isViewMode, false);
            }
          },
          error: (error) => errorSubscription(500, String(error), showNotifications),
        });
      },
      [FloorOutboundEvent.ROOM_CHANGED]: ({room_id}: RoomChangedDataType) => {
        roomsApi.getRoomById(String(room_id)).subscribe({
          next: (room) => editRoomSubscription(room, showNotifications),
          error: (error) => errorSubscription(500, String(error), showNotifications),
        });
      },
      [FloorOutboundEvent.ROOM_DELETED]: ({room_id, door_ids}: RoomDeletedDataType) => {
        reconcileAfterRoomDelete(String(room_id), door_ids || []);
      },
      [FloorOutboundEvent.DOOR_DELETED]: ({door_id}: DoorDeletedDataType) => {
        deleteDoorSubscription(String(door_id));
      },
      [FloorOutboundEvent.ERROR]: ({error, status_code}: ErrorDataType) => {
        errorSubscription(status_code, error, showNotifications);
      },
      [FloorOutboundEvent.UPDATE_MEETINGROOM_STATUS]: ({meetingroom}: UpdatedMeetingRoomDataType) => {
        updateMeetingRoomInRooms(meetingroom);
      },
      [FloorOutboundEvent.MEETINGROOM_UPDATED]: ({meetingroom}: UpdatedMeetingRoomDataType) => {
        updateMeetingRoomInRooms(meetingroom);
      },
      downloadCSVReport: getCSVReport,
      downloadPDFReport: getPDFReport,
      downloadXLSXReport: getXLSXReport,
      bookWorkspace: bookChosenWorkspace,
      editWorkspace: handleEditWorkspace,
      editRoom: handleEditRoom,
      deleteWorkspace: handleDeleteWorkspace,
      deleteDoor: handleDeleteDoor,
      refreshWorkspaces: () => refreshWorkspaces(),
      applyWorkspaceCreate,
      applyWorkspaceUpdate: (workspace: WorkspaceResponse) => applyWorkspaceUpdate(workspace),
    };
  }, [
    bookChosenWorkspace,
    refreshWorkspaces,
    isBookingModeActive,
    editRoomSubscription,
    deleteDoorSubscription,
    editWorkspaceSubscription,
    createWorkspaceSubscription,
    consumePendingWorkspace,
    applyWorkspaceCreate,
    applyWorkspaceUpdate,
    workspaces$,
    errorSubscription,
    getCSVReport,
    getPDFReport,
    getXLSXReport,
    handleEditRoom,
    handleEditWorkspace,
    handleDeleteWorkspace,
    handleDeleteDoor,
    isViewMode,
    roomsApi,
    showNotifications,
    updateMeetingRoomInRooms,
    workspacesApi,
    reconcileAfterRoomDelete,
  ]);

  // Handle socket events
  useFloorEvents(eventsMap, floorsEventEmitter, isClientReadyState);

  const abortChanges = useCallback((): Observable<void> => {
    const currentWorkspaces = workspaces$.getValue().filter((element) => !element.data?.isDraft);
    workspaces$.next(currentWorkspaces);

    const workspacesToUpdate = buildAbortWorkspaceUpdates({
      initialWorkspaces,
      currentWorkspaces,
      workspacesToRevert,
    });
    const workspacesToDelete = createdWorkspaces.map(({data}) => Number(data.id));
    const workspacesToCreate = deletedWorkspaces.map(({data}) => ({
      ...toAbortUpdatePayload(data),
      floor: Number(floorId),
    }));

    const hasMutations =
      workspacesToUpdate.length > 0 || workspacesToDelete.length > 0 || workspacesToCreate.length > 0;

    if (!hasMutations) {
      createdWorkspaces$.next([]);
      deletedWorkspaces$.next([]);
      setWorkspacesToRevert([]);
      resetValidation();
      return of(undefined);
    }

    beginWorkspaceMutation();

    return combineLatest([
      workspacesToUpdate.length > 0 ? workspacesApi.updateWorkspaces(workspacesToUpdate) : of({}),
      workspacesToDelete.length > 0 ? workspacesApi.deleteWorkspaces(workspacesToDelete.map(String)) : of({}),
      workspacesToCreate.length > 0 ? workspacesApi.createWorkspaces(workspacesToCreate) : of({}),
    ]).pipe(
      map(() => undefined),
      tap({
        next: () => setWorkspacesToRevert([]),
        error: (error) => errorSubscription(500, String(error), showNotifications),
      }),
      finalize(() => {
        endWorkspaceMutation();
        refreshWorkspaces();
        createdWorkspaces$.next([]);
        deletedWorkspaces$.next([]);
        resetValidation();
      }),
    );
  }, [
    initialWorkspaces,
    createdWorkspaces,
    deletedWorkspaces,
    workspacesApi,
    refreshWorkspaces,
    createdWorkspaces$,
    deletedWorkspaces$,
    workspacesToRevert,
    errorSubscription,
    showNotifications,
    floorId,
    workspaces$,
    resetValidation,
    beginWorkspaceMutation,
    endWorkspaceMutation,
  ]);

  const hasDraftWorkspace = useMemo(() => Boolean(workspaces?.some((element) => element.data?.isDraft)), [workspaces]);

  const saveChanges = useCallback(() => {
    if (hasDraftWorkspace || hasInvalidWorkspaces) {
      floorsModalsFacade.openInfoModal(t('modals.invalidWorkspaceMessage'), t('modals.attention'), t('buttons.ok'));
      return false;
    }
    initialWorkspaces$.next(workspaces);
    createdWorkspaces$.next([]);
    deletedWorkspaces$.next([]);
    refreshWorkspaces();
    setWorkspacesToRevert([]);
    resetValidation();
  }, [
    hasDraftWorkspace,
    hasInvalidWorkspaces,
    initialWorkspaces$,
    workspaces,
    createdWorkspaces$,
    deletedWorkspaces$,
    refreshWorkspaces,
    resetValidation,
    floorsModalsFacade,
    t,
  ]);

  const handleSearchFloorSubmit = useCallback(
    (data: SearchFloorValues) => {
      setIsPlanLoading(true);
      navigate(`/floors/${data.floor}`);
    },
    [navigate, setIsPlanLoading],
  );

  const handleSecondPlanFloorChange = useCallback(() => {
    setIsPlanLoading(true);
  }, [setIsPlanLoading]);

  const {elements} = useFlowElements(workspaces, rooms, planElements);

  useEffect(() => {
    floorsEventEmitter.connect(floorId);
    return () => {
      setIsClientReadyState(false);
      floorsEventEmitter.close();
    };
  }, [floorsEventEmitter, floorId]);

  useEffect(() => {
    if (!isClientReadyState) {
      return;
    }

    const interval = setInterval(() => {
      refreshWorkspaces();
    }, 4 * 60 * 1000);

    return () => clearInterval(interval);
  }, [refreshWorkspaces, isClientReadyState]);

  useEffect(() => {
    applyViewMode(isViewMode);
  }, [isViewMode, applyViewMode]);

  const handleAddNewWorkspace = useCallback(
    (isVirtual: boolean, rotateDegree: number = 0) => {
      if (workspaces$.getValue().some((element) => element.data?.isDraft)) {
        return;
      }

      const viewportCenter = getViewportCenter();
      let x = 0;
      let y = 0;

      if (viewportCenter) {
        x = viewportCenter.x - 50;
        y = viewportCenter.y - 25;
      } else {
        const existing = workspaces$.getValue();
        const roomElements = rooms$.getValue();
        if (existing.length > 0) {
          const [cx, cy] = existing[0].data.coordinates[0];
          x = cx - 50;
          y = cy - 25;
        } else if (roomElements.length > 0 && 'position' in roomElements[0]) {
          x = roomElements[0].position.x + 30;
          y = roomElements[0].position.y + 30;
        }
      }

      const draftNode = createDraftWorkspaceNode(isVirtual, x, y, rotateDegree, isViewMode);
      addDraftWorkspace(draftNode);
    },
    [addDraftWorkspace, getViewportCenter, isViewMode, rooms$, workspaces$],
  );

  return {
    flowElements: elements,
    workspaceElements: workspaces,
    roomElements: rooms,
    floor,
    handleAddNewWorkspace,
    isPlanLoading,
    isAttendanceLoading,
    isPlanExists,
    reactFlowWrapper,
    onLoad: loadHandler,
    onDragOver: dragOverHandler,
    onDrop: dragHandler,
    handleUploadPlan: uploadPlanHandler,
    onNodeDragStart: nodeDragStartHandler,
    onNodeDragStop: nodeDragStopHandler,
    handleSearchFloorSubmit,
    handleBookingWorkspaceMode: bookingWorkspaceMode,
    handleBookWorkspace: bookWorkspace,
    abortChanges,
    saveChanges,
    selectable,
    floorsEventEmitter,
    isClientReadyState,
    handleSecondPlanFloorChange,
    isWorkspaceMutationPending,
    beginWorkspaceMutation,
    endWorkspaceMutation,
    hasDraftWorkspace,
  };
};
